import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioItemIcon } from "web_myplann/Portfolio/Components/PortfolioItemIcon";
import { PortfolioRow } from "web_myplann/Portfolio/Components/Redesign/ListItem/PortfolioRow";
import { PortfolioRowTrailing } from "web_myplann/Portfolio/Components/Redesign/ListItem/PortfolioRowTrailing";
import { propertySubtitle } from "web_myplann/Portfolio/Components/Redesign/ListItem/propertyHelpers";
import { propertyListTitle } from "web_myplann/Portfolio/Components/Redesign/ListItem/propertyListTitle";
import { getPortfolioCurrencySign } from "web_myplann/Portfolio/Helpers/getPortfolioCurrencySign";
import { getPortfolioTileLabel } from "web_myplann/Portfolio/Helpers/getPortfolioTileLabel";
import { getPortfolioTileTrailing } from "web_myplann/Portfolio/Helpers/getPortfolioTileTrailing";
import { getPropertyTileTrailing } from "web_myplann/Portfolio/Helpers/getPropertyTileTrailing";
import type { CategoryItem } from "web_myplann/Portfolio/Types/portfolioCategory";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

type Props = {
	item: CategoryItem;
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined;
};

export const PortfolioListItemRedesign: FC<Props> = ({ item, summaryData }) => {
	const navigate = useNavigate();
	const { t } = useResource();

	if (item.kind === PortfolioItemKind.Property) {
		const property = item.data;
		const trailing = getPropertyTileTrailing(property);
		return (
			<PortfolioRow
				icon={<PortfolioItemIcon propertyType={property.property} />}
				title={propertyListTitle(property, t)}
				subtitle={propertySubtitle(property)}
				isInvestment={property.forInvestments ?? false}
				isMissingValue={trailing.isMissingValue}
				right={<PortfolioRowTrailing trailing={trailing} />}
				onClick={() =>
					navigate(
						Routing.Portfolio.PropertyDetail.path(property.id.toString()),
					)
				}
			/>
		);
	}

	const portfolio = item.data;
	const label = getPortfolioTileLabel(portfolio, t);
	const trailing = getPortfolioTileTrailing(portfolio, summaryData);

	return (
		<PortfolioRow
			icon={
				<PortfolioItemIcon domain={portfolio.domain} logo={portfolio.icon} />
			}
			title={label.title}
			subtitle={label.subtitle}
			isMissingValue={trailing.isMissingValue}
			right={
				<PortfolioRowTrailing
					trailing={trailing}
					currencySign={
						trailing.currency
							? getPortfolioCurrencySign(t, trailing.currency)
							: (portfolio.currencySign ?? undefined)
					}
				/>
			}
			onClick={() =>
				navigate(
					Routing.Portfolio.PortfolioDetail.path(portfolio.id.toString()),
				)
			}
		/>
	);
};
